/**
 * AffDice SuperAdmin messages — the words screens.ts builds — labels, verdicts and sentences shared by several screens.
 *
 * Every key starts `view.` so no other catalogue can collide with it.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const VIEW_MESSAGES = {
  'view.chain.empty': {
    en: 'There are no audit records to verify. That is not the same as a chain that verifies — an emptied log passes every check that only looks at what is there.',
    es: 'No hay registros de auditoría que verificar. Eso no es lo mismo que una cadena que se verifica: un registro vaciado pasa todas las comprobaciones que solo miran lo que hay.',
    'pt-BR': 'Não há registros de auditoria para verificar. Isso não é o mesmo que uma cadeia que se verifica: um registro esvaziado passa por todas as verificações que só olham o que existe.',
  },
  'view.consumer.failed.one': {
    en: '{count} event could not be handled. The platform quarantines rather than skipping — a consumer that advances past what it could not process loses the event, and the affiliate loses the commission with no record of why.',
    es: '{count} evento no se pudo procesar. La plataforma lo aísla en lugar de omitirlo: un consumidor que avanza más allá de lo que no pudo procesar pierde el evento, y el afiliado pierde la comisión sin ningún registro del motivo.',
    'pt-BR': '{count} evento não pôde ser tratado. A plataforma o coloca em quarentena em vez de ignorá-lo: um consumidor que avança além do que não conseguiu processar perde o evento, e o afiliado perde a comissão sem nenhum registro do motivo.',
  },
  'view.consumer.failed.other': {
    en: '{count} events could not be handled. The platform quarantines rather than skipping — a consumer that advances past what it could not process loses the event, and the affiliate loses the commission with no record of why.',
    es: '{count} eventos no se pudieron procesar. La plataforma los aísla en lugar de omitirlos: un consumidor que avanza más allá de lo que no pudo procesar pierde el evento, y el afiliado pierde la comisión sin ningún registro del motivo.',
    'pt-BR': '{count} eventos não puderam ser tratados. A plataforma os coloca em quarentena em vez de ignorá-los: um consumidor que avança além do que não conseguiu processar perde o evento, e o afiliado perde a comissão sem nenhum registro do motivo.',
  },
  'view.consumer.stalled': {
    en: 'No movement in {hours} hours. Nothing throws when a consumer stops; this is the only place it shows.',
    es: 'Sin movimiento en {hours} horas. Nada falla cuando un consumidor se detiene; este es el único lugar donde se nota.',
    'pt-BR': 'Sem movimento há {hours} horas. Nada dá erro quando um consumidor para; este é o único lugar onde isso aparece.',
  },
  'view.consumer.notStarted.one': {
    en: 'Running, but it has reached none of the {waiting} event waiting for it.',
    es: 'En marcha, pero no ha llegado a ninguno de los {waiting} evento que lo esperan.',
    'pt-BR': 'Em execução, mas não chegou a nenhum dos {waiting} evento que o aguardam.',
  },
  'view.consumer.notStarted.other': {
    en: 'Running, but it has reached none of the {waiting} events waiting for it.',
    es: 'En marcha, pero no ha llegado a ninguno de los {waiting} eventos que lo esperan.',
    'pt-BR': 'Em execução, mas não chegou a nenhum dos {waiting} eventos que o aguardam.',
  },
  'view.condition.noWaiveFeed': {
    en: 'Cannot be waived. It is the only defence against a silently mis-mapped revenue feed.',
    es: 'No se puede dispensar. Es la única defensa contra un flujo de ingresos mal mapeado sin que nadie lo note.',
    'pt-BR': 'Não pode ser dispensada. É a única defesa contra um feed de receita mapeado incorretamente sem que ninguém perceba.',
  },
  'view.condition.noWaiveRetention': {
    en: 'Cannot be waived. It is the only thing between indefinite retention and a live data subject.',
    es: 'No se puede dispensar. Es lo único que separa la conservación indefinida de un titular de datos vigente.',
    'pt-BR': 'Não pode ser dispensada. É a única coisa que separa a retenção indefinida de um titular de dados ativo.',
  },
  'view.gate.allPass': {
    en: 'Every condition passes. This operator can go live.',
    es: 'Se cumplen todas las condiciones. Este operador puede entrar en marcha.',
    'pt-BR': 'Todas as condições foram cumpridas. Este operador pode entrar em operação.',
  },
  'view.gate.blocked': {
    en: '{met} of {total} pass. {blocked} of the outstanding ones cannot be waived by anyone, for any launch date .',
    es: '{met} de {total} se cumplen. {blocked} de las pendientes no las puede dispensar nadie, para ninguna fecha de lanzamiento .',
    'pt-BR': '{met} de {total} foram cumpridas. {blocked} das pendentes não podem ser dispensadas por ninguém, para nenhuma data de lançamento .',
  },
  'view.gate.waivable': {
    en: '{met} of {total} pass. The rest may be waived with a named approver, a stated reason and an expiry — recorded against this operator, not agreed in a meeting.',
    es: '{met} de {total} se cumplen. El resto se puede dispensar con un aprobador identificado, un motivo y un vencimiento: queda registrado en este operador, no acordado en una reunión.',
    'pt-BR': '{met} de {total} foram cumpridas. As demais podem ser dispensadas com um aprovador identificado, um motivo e uma validade: fica registrado neste operador, não combinado em uma reunião.',
  },
  'view.lifecycle.meaning.setup': {
    en: 'Being set up. Events bill and commission settles once brands, feeds and deals are configured.',
    es: 'En configuración. Los eventos se facturan y la comisión se liquida cuando se configuran las marcas, los flujos y los acuerdos.',
    'pt-BR': 'Em configuração. Os eventos são faturados e a comissão é liquidada quando as marcas, os feeds e os acordos estão configurados.',
  },
  'view.lifecycle.meaning.live': {
    en: 'Events are billed and commission settles.',
    es: 'Los eventos se facturan y la comisión se liquida.',
    'pt-BR': 'Os eventos são faturados e a comissão é liquidada.',
  },
  'view.lifecycle.meaning.restricted': {
    en: 'Live, but in arrears. Ingestion, computation and period close continue regardless — stopping them would strand an unbilled period.',
    es: 'En marcha, pero con pagos atrasados. La ingesta, el cálculo y el cierre de período continúan igualmente: detenerlos dejaría un período sin facturar.',
    'pt-BR': 'Em operação, mas com pagamento em atraso. A ingestão, o cálculo e o fechamento do período continuam mesmo assim: interrompê-los deixaria um período sem faturar.',
  },
  'view.lifecycle.meaning.suspended': {
    en: 'Suspended for cause. Ingestion, computation and period close continue regardless — stopping them would strand an unbilled period. New brands, campaigns and links are blocked, and this operator\'s own staff cannot sign in until it is reactivated.',
    es: 'Suspendido por causa justificada. La ingesta, el cálculo y el cierre de período continúan igualmente: detenerlos dejaría un período sin facturar. Se bloquean las marcas, campañas y enlaces nuevos, y el personal de este operador no puede iniciar sesión hasta que se reactive.',
    'pt-BR': 'Suspenso por justa causa. A ingestão, o cálculo e o fechamento do período continuam mesmo assim: interrompê-los deixaria um período sem faturar. Novas marcas, campanhas e links ficam bloqueados, e a própria equipe deste operador não consegue entrar até que seja reativado.',
  },
  'view.lifecycle.label.prospect': {
    en: 'Prospect',
    es: 'Prospecto',
    'pt-BR': 'Prospecto',
  },
  'view.lifecycle.label.configuring': {
    en: 'Configuring',
    es: 'En configuración',
    'pt-BR': 'Em configuração',
  },
  'view.lifecycle.label.live': {
    en: 'Live',
    es: 'En marcha',
    'pt-BR': 'Em operação',
  },
  'view.lifecycle.label.restricted': {
    en: 'Restricted',
    es: 'Restringido',
    'pt-BR': 'Restrito',
  },
  'view.lifecycle.label.suspended': {
    en: 'Suspended',
    es: 'Suspendido',
    'pt-BR': 'Suspenso',
  },
  'view.lifecycle.label.abandoned': {
    en: 'Abandoned',
    es: 'Abandonado',
    'pt-BR': 'Abandonado',
  },
  'view.lifecycle.label.softDeleted': {
    en: 'Soft-deleted',
    es: 'Eliminado (recuperable)',
    'pt-BR': 'Excluído (recuperável)',
  },
  'view.lifecycle.label.purged': {
    en: 'Purged',
    es: 'Purgado',
    'pt-BR': 'Eliminado definitivamente',
  },
  'view.invoice.number': {
    en: 'Number',
    es: 'Número',
    'pt-BR': 'Número',
  },
  'view.invoice.period': {
    en: '{start} to {end}',
    es: '{start} a {end}',
    'pt-BR': '{start} a {end}',
  },
  'view.invoice.net': {
    en: 'Net',
    es: 'Neto',
    'pt-BR': 'Líquido',
  },
  'view.invoice.tax': {
    en: 'Tax',
    es: 'Impuesto',
    'pt-BR': 'Imposto',
  },
  'view.invoice.gross': {
    en: 'Gross',
    es: 'Bruto',
    'pt-BR': 'Bruto',
  },
  'view.affiliateStatus.pending': {
    en: 'Pending',
    es: 'Pendiente',
    'pt-BR': 'Pendente',
  },
  'view.affiliateStatus.active': {
    en: 'Active',
    es: 'Activo',
    'pt-BR': 'Ativo',
  },
  'view.affiliateStatus.underReview': {
    en: 'Under Review',
    es: 'En revisión',
    'pt-BR': 'Em análise',
  },
  'view.affiliateStatus.suspended': {
    en: 'Suspended',
    es: 'Suspendido',
    'pt-BR': 'Suspenso',
  },
  'view.affiliateStatus.blocked': {
    en: 'Blocked',
    es: 'Bloqueado',
    'pt-BR': 'Bloqueado',
  },
  'view.affiliateStatus.rejected': {
    en: 'Rejected',
    es: 'Rechazado',
    'pt-BR': 'Rejeitado',
  },
  'view.affiliateStatus.terminated': {
    en: 'Terminated',
    es: 'Finalizado',
    'pt-BR': 'Encerrado',
  },
  'view.affiliateStatus.deactivated': {
    en: 'Deactivated',
    es: 'Desactivado',
    'pt-BR': 'Desativado',
  },
  'view.toggle.deactivate': {
    en: '{label}. Deactivate them on this operator.',
    es: '{label}. Desactívalo en este operador.',
    'pt-BR': '{label}. Desative-o neste operador.',
  },
  'view.toggle.reactivate': {
    en: '{label}. Reactivate them on this operator.',
    es: '{label}. Reactívalo en este operador.',
    'pt-BR': '{label}. Reative-o neste operador.',
  },
  'view.toggle.theirs': {
    en: '{label}. This state is their own operator\'s to change.',
    es: '{label}. Este estado lo cambia su propio operador.',
    'pt-BR': '{label}. Este estado é de responsabilidade do próprio operador.',
  },
  'view.figure.avgFtd': {
    en: 'Avg FTD size',
    es: 'Tamaño medio de FTD',
    'pt-BR': 'Tamanho médio de FTD',
  },
  'view.figure.avgDeposit': {
    en: 'Avg deposit',
    es: 'Depósito medio',
    'pt-BR': 'Depósito médio',
  },
  'view.figure.ggrPerCustomer': {
    en: 'GGR / customer',
    es: 'GGR / cliente',
    'pt-BR': 'GGR / cliente',
  },
  'view.figure.ggrMinusBonusPerCustomer': {
    en: 'Commissionable NGR / customer',
    es: 'NGR comisionable / cliente',
    'pt-BR': 'NGR comissionável / cliente',
  },
  'view.figure.outstanding': {
    en: 'Outstanding payable',
    es: 'Pendiente de pago',
    'pt-BR': 'A pagar pendente',
  },
  'view.figure.paid': {
    en: 'Paid',
    es: 'Pagado',
    'pt-BR': 'Pago',
  },
  'view.figure.clicks': {
    en: 'Clicks',
    es: 'Clics',
    'pt-BR': 'Cliques',
  },
  'view.figure.registrations': {
    en: 'Registrations',
    es: 'Registros',
    'pt-BR': 'Cadastros',
  },
  'view.figure.ftds': {
    en: 'FTDs',
    es: 'FTDs',
    'pt-BR': 'FTDs',
  },
  'view.figure.activities': {
    en: 'Activities',
    es: 'Actividades',
    'pt-BR': 'Atividades',
  },
  'view.figure.registrationCr': {
    en: 'Registration CR',
    es: 'Conversión de registro',
    'pt-BR': 'Conversão de cadastro',
  },
  'view.figure.totalFtdAmount': {
    en: 'Total FTD amount',
    es: 'Importe total de FTD',
    'pt-BR': 'Valor total de FTD',
  },
  'view.figure.deposits': {
    en: 'Deposits',
    es: 'Depósitos',
    'pt-BR': 'Depósitos',
  },
  'view.figure.totalDepositsCount': {
    en: 'Total deposits (count)',
    es: 'Total de depósitos (cantidad)',
    'pt-BR': 'Total de depósitos (quantidade)',
  },
  'view.figure.ggr': {
    en: 'GGR',
    es: 'GGR',
    'pt-BR': 'GGR',
  },
  'view.figure.ggrMinusBonus': {
    en: 'Commissionable NGR',
    es: 'NGR comisionable',
    'pt-BR': 'NGR comissionável',
  },
  'view.group.earnings': {
    en: 'Earnings & Payouts',
    es: 'Ganancias y pagos',
    'pt-BR': 'Ganhos e pagamentos',
  },
  'view.group.insights': {
    en: 'Customer Insights, Traffic & Activity',
    es: 'Clientes, tráfico y actividad',
    'pt-BR': 'Clientes, tráfego e atividade',
  },
  'view.group.conversion': {
    en: 'Conversion & Funnel',
    es: 'Conversión y embudo',
    'pt-BR': 'Conversão e funil',
  },
  'view.group.transactions': {
    en: 'Transaction Metrics',
    es: 'Métricas de transacciones',
    'pt-BR': 'Métricas de transações',
  },
  'view.group.revenue': {
    en: 'Revenue & Value',
    es: 'Ingresos y valor',
    'pt-BR': 'Receita e valor',
  },
  'view.group.averages': {
    en: 'Averages & KPIs',
    es: 'Promedios e indicadores',
    'pt-BR': 'Médias e indicadores',
  },
  'view.group.noAverages': {
    en: 'No averages yet — each needs at least one FTD, deposit or customer to divide by.',
    es: 'Aún no hay promedios: cada uno necesita al menos un FTD, depósito o cliente para dividir.',
    'pt-BR': 'Ainda não há médias: cada uma precisa de pelo menos um FTD, depósito ou cliente para dividir.',
  },
  'view.dashboard.operators': {
    en: 'Operators',
    es: 'Operadores',
    'pt-BR': 'Operadores',
  },
  'view.dashboard.affiliates': {
    en: 'Affiliates (platform-wide)',
    es: 'Afiliados (en toda la plataforma)',
    'pt-BR': 'Afiliados (em toda a plataforma)',
  },
  'view.dashboard.blockedGoLive': {
    en: 'Operators blocked on go-live',
    es: 'Operadores bloqueados en la puesta en marcha',
    'pt-BR': 'Operadores bloqueados na entrada em operação',
  },
  'view.dashboard.auditChain': {
    en: 'Audit chain',
    es: 'Cadena de auditoría',
    'pt-BR': 'Cadeia de auditoria',
  },
  'view.dashboard.verifies': {
    en: 'Verifies',
    es: 'Se verifica',
    'pt-BR': 'Verifica',
  },
  'view.dashboard.notVerifies': {
    en: 'Does not verify',
    es: 'No se verifica',
    'pt-BR': 'Não verifica',
  },
  'view.dashboard.consumersMoving': {
    en: 'Consumers moving',
    es: 'Consumidores en movimiento',
    'pt-BR': 'Consumidores em movimento',
  },
  'view.dashboard.consumersNotMoving': {
    en: 'Consumers not moving',
    es: 'Consumidores sin movimiento',
    'pt-BR': 'Consumidores sem movimento',
  },
  'view.dashboard.eventsIngested': {
    en: 'Events ingested (24h)',
    es: 'Eventos ingeridos (24 h)',
    'pt-BR': 'Eventos ingeridos (24 h)',
  },
  'view.dashboard.clicksDays': {
    en: 'Clicks ({days}d)',
    es: 'Clics ({days} d)',
    'pt-BR': 'Cliques ({days} d)',
  },
  'view.dashboard.registrationsDays': {
    en: 'Registrations ({days}d)',
    es: 'Registros ({days} d)',
    'pt-BR': 'Cadastros ({days} d)',
  },
  'view.dashboard.ftdsDays': {
    en: 'FTDs ({days}d)',
    es: 'FTDs ({days} d)',
    'pt-BR': 'FTDs ({days} d)',
  },
  'view.dashboard.billingNote': {
    en: 'Grouped by currency, never summed across them — each operator’s platform invoice carries its own base currency.',
    es: 'Agrupado por moneda, nunca sumado entre ellas: la factura de plataforma de cada operador lleva su propia moneda base.',
    'pt-BR': 'Agrupado por moeda, nunca somado entre elas: a fatura de plataforma de cada operador tem a sua própria moeda base.',
  },
  'view.period.commissionLine.one': {
    en: '{count} commission line',
    es: '{count} línea de comisión',
    'pt-BR': '{count} linha de comissão',
  },
  'view.period.commissionLine.other': {
    en: '{count} commission lines',
    es: '{count} líneas de comisión',
    'pt-BR': '{count} linhas de comissão',
  },
  'view.period.statement.one': {
    en: '{count} statement',
    es: '{count} estado de cuenta',
    'pt-BR': '{count} extrato',
  },
  'view.period.statement.other': {
    en: '{count} statements',
    es: '{count} estados de cuenta',
    'pt-BR': '{count} extratos',
  },
  'view.period.invoice.one': {
    en: '{count} invoice',
    es: '{count} factura',
    'pt-BR': '{count} fatura',
  },
  'view.period.invoice.other': {
    en: '{count} invoices',
    es: '{count} facturas',
    'pt-BR': '{count} faturas',
  },
  'view.period.payoutDocument.one': {
    en: '{count} payout document',
    es: '{count} documento de pago',
    'pt-BR': '{count} documento de pagamento',
  },
  'view.period.payoutDocument.other': {
    en: '{count} payout documents',
    es: '{count} documentos de pago',
    'pt-BR': '{count} documentos de pagamento',
  },
  'view.period.dispute.one': {
    en: '{count} dispute',
    es: '{count} disputa',
    'pt-BR': '{count} contestação',
  },
  'view.period.dispute.other': {
    en: '{count} disputes',
    es: '{count} disputas',
    'pt-BR': '{count} contestações',
  },
  'view.period.platformInvoice.one': {
    en: '{count} platform invoice',
    es: '{count} factura de plataforma',
    'pt-BR': '{count} fatura de plataforma',
  },
  'view.period.platformInvoice.other': {
    en: '{count} platform invoices',
    es: '{count} facturas de plataforma',
    'pt-BR': '{count} faturas de plataforma',
  },
  'view.period.summaryDocumented': {
    en: 'Documents were issued from this period, so an affiliate already holds figures the platform will no longer have. Recomputing the month produces new documents with new numbers, not these ones back.',
    es: 'Se emitieron documentos de este período, así que un afiliado ya tiene cifras que la plataforma dejará de tener. Recalcular el mes produce documentos nuevos con números nuevos, no devuelve estos.',
    'pt-BR': 'Foram emitidos documentos deste período, então um afiliado já tem valores que a plataforma deixará de ter. Recalcular o mês gera documentos novos com números novos, não estes de volta.',
  },
  'view.period.summaryComputed': {
    en: 'Only the platform’s own figures are in here. Calculating the month again produces them afresh.',
    es: 'Aquí solo están las cifras propias de la plataforma. Volver a calcular el mes las genera de nuevo.',
    'pt-BR': 'Aqui há apenas os valores da própria plataforma. Calcular o mês novamente os gera do zero.',
  },
  'view.period.summaryEmpty': {
    en: 'Nothing was ever computed in this period. Deleting it frees the month to be opened again.',
    es: 'Nunca se calculó nada en este período. Eliminarlo deja el mes libre para abrirlo de nuevo.',
    'pt-BR': 'Nada foi calculado neste período. Excluí-lo libera o mês para ser aberto novamente.',
  },
  'view.setting.groupClickBot': {
    en: 'Click and bot limits',
    es: 'Límites de clics y bots',
    'pt-BR': 'Limites de cliques e bots',
  },
  'view.setting.groupOther': {
    en: 'Other settings',
    es: 'Otros ajustes',
    'pt-BR': 'Outras configurações',
  },
  'view.setting.seconds': {
    en: '{value} seconds',
    es: '{value} segundos',
    'pt-BR': '{value} segundos',
  },
  'view.setting.clicksPerHour': {
    en: '{value} clicks per hour',
    es: '{value} clics por hora',
    'pt-BR': '{value} cliques por hora',
  },
  'view.setting.hours': {
    en: '{value} hours',
    es: '{value} horas',
    'pt-BR': '{value} horas',
  },
  'view.setting.clicks': {
    en: '{value} clicks',
    es: '{value} clics',
    'pt-BR': '{value} cliques',
  },
  'view.setting.changesTo': {
    en: 'Changes to {value} on {day}.',
    es: 'Cambia a {value} el {day}.',
    'pt-BR': 'Muda para {value} em {day}.',
  },
  'view.setting.state.scheduled': {
    en: 'Scheduled',
    es: 'Programado',
    'pt-BR': 'Agendado',
  },
  'view.setting.state.inForce': {
    en: 'In force',
    es: 'En vigor',
    'pt-BR': 'Em vigor',
  },
  'view.setting.state.ended': {
    en: 'Ended',
    es: 'Finalizado',
    'pt-BR': 'Encerrado',
  },
  'view.setting.state.withdrawn': {
    en: 'Withdrawn',
    es: 'Retirado',
    'pt-BR': 'Retirado',
  },
  'view.referral.off': {
    en: 'Off — this operator has not turned sub-affiliate recruiting on.',
    es: 'Desactivado: este operador no ha activado el reclutamiento de subafiliados.',
    'pt-BR': 'Desativado: este operador não ativou o recrutamento de subafiliados.',
  },
  'view.referral.on.one': {
    en: 'On — up to {count} level deep, {override} default override, capped at {cap}.',
    es: 'Activado: hasta {count} nivel de profundidad, {override} de anulación predeterminada, con un tope de {cap}.',
    'pt-BR': 'Ativado: até {count} nível de profundidade, {override} de override padrão, limitado a {cap}.',
  },
  'view.referral.on.other': {
    en: 'On — up to {count} levels deep, {override} default override, capped at {cap}.',
    es: 'Activado: hasta {count} niveles de profundidad, {override} de anulación predeterminada, con un tope de {cap}.',
    'pt-BR': 'Ativado: até {count} níveis de profundidade, {override} de override padrão, limitado a {cap}.',
  },
  'view.referral.ofNgr': {
    en: '{rate} of Commissionable NGR',
    es: '{rate} del NGR comisionable',
    'pt-BR': '{rate} do NGR comissionável',
  },
  'view.referral.ofCommission': {
    en: '{rate} of commission',
    es: '{rate} de la comisión',
    'pt-BR': '{rate} da comissão',
  },
  'view.referral.fixedTerm': {
    en: 'Fixed term',
    es: 'Plazo fijo',
    'pt-BR': 'Prazo fixo',
  },
  'view.referral.lifetime': {
    en: 'Lifetime',
    es: 'De por vida',
    'pt-BR': 'Vitalício',
  },
  'view.referral.whileActive': {
    en: 'While active',
    es: 'Mientras esté activo',
    'pt-BR': 'Enquanto ativo',
  },
} as const satisfies Catalogue;
